<template>
    <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
        <h3 class="text-lg font-semibold text-gray-900">Your subscriptions</h3>
        <p class="mt-1 text-sm text-gray-600">Manage your current RentRisk subscriptions.</p>

        <div class="mt-4 overflow-x-auto">
            <div class="align-middle inline-block min-w-full">
                <div class="shadow overflow-hidden border rounded-lg">
                    <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-blue-50">
                            <tr>
                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Service</th>
                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Status</th>
                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Next Payment</th>
                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Amount</th>
                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <!-- Rent Payments -->
                            <template v-if="rentPaymentSubscription">
                                <tr class="hover:bg-gray-50" :class="{ 'opacity-60': hasActiveProtectSubscription }">
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-8 w-8">
                                                <font-awesome-icon :icon="faCommentsDollar" class="text-blue-600" />
                                            </div>
                                            <div class="ml-4">
                                                <div class="text-sm font-semibold text-gray-900">Rent Payments</div>
                                                <div class="text-sm text-gray-500">
                                                    <span v-if="rentPaymentHasPropertyList">Per-property subscription</span>
                                                    <span v-else>A la carte subscription</span>
                                                    <span v-if="hasActiveProtectSubscription" class="text-purple-600 font-medium">(Included in RiskGuard)</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <span :class="rentPaymentSubscription.is_cancellation_requested ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'"
                                            class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                                            {{ rentPaymentSubscription.is_cancellation_requested ? 'Cancellation Requested' : 'Active' }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="rentPaymentSubscription.is_cancellation_requested">
                                            Cancels on {{ rentPaymentSubscription.cancel_at ? new Date(rentPaymentSubscription.cancel_at).toLocaleDateString() : rentPaymentSubscription.stripe_sub_ends_at_formatted }}
                                        </span>
                                        <span v-else>{{ rentPaymentSubscription.stripe_sub_ends_at_formatted }}</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${{ rentPaymentTotalAmount }}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div class="flex space-x-2">
                                            <button v-if="!rentPaymentSubscription.is_cancellation_requested && !rentPaymentHasPropertyList"
                                                    :disabled="isSubmitting || hasActiveProtectSubscription"
                                                    class="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    @click="$emit('cancel', 'rent_payment')">
                                                Cancel
                                            </button>
                                            <button v-else-if="rentPaymentSubscription.is_cancellation_requested"
                                                    :disabled="isSubmitting || hasActiveProtectSubscription"
                                                    class="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    @click="$emit('resume-rent-payment')">
                                                Resume
                                            </button>
                                            <span v-if="hasActiveProtectSubscription" class="text-xs text-gray-500 italic">Included in RiskGuard</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-for="prop in rentPaymentFeature?.properties || []" :key="`rp-prop-${prop.id}`" class="bg-gray-50">
                                    <td class="pl-16 pr-6 py-3 whitespace-nowrap text-sm text-gray-700">{{ prop.full_address }}</td>
                                    <td colspan="3" class="px-6 py-3"></td>
                                    <td class="px-6 py-3 whitespace-nowrap text-sm font-medium">
                                        <button :disabled="isSubmitting"
                                                class="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="$emit('cancel-property', 'rent_payment', prop)">
                                            Cancel
                                        </button>
                                    </td>
                                </tr>
                            </template>

                            <!-- Maintenance -->
                            <template v-if="maintenanceSubscription">
                                <tr class="hover:bg-gray-50" :class="{ 'opacity-60': hasActiveProtectSubscription }">
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-8 w-8">
                                                <font-awesome-icon :icon="faWrench" class="text-orange-600" />
                                            </div>
                                            <div class="ml-4">
                                                <div class="text-sm font-semibold text-gray-900">Maintenance</div>
                                                <div class="text-sm text-gray-500">
                                                    <span v-if="maintenanceHasPropertyList">Per-property subscription</span>
                                                    <span v-else>A la carte subscription</span>
                                                    <span v-if="hasActiveProtectSubscription" class="text-purple-600 font-medium">(Included in RiskGuard)</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <span :class="maintenanceSubscription.is_cancellation_requested ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'"
                                            class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                                            {{ maintenanceSubscription.is_cancellation_requested ? 'Cancellation Requested' : 'Active' }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="maintenanceSubscription.is_cancellation_requested">
                                            Cancels on {{ maintenanceSubscription.cancel_at ? new Date(maintenanceSubscription.cancel_at).toLocaleDateString() : maintenanceSubscription.stripe_sub_ends_at_formatted }}
                                        </span>
                                        <span v-else>{{ maintenanceSubscription.stripe_sub_ends_at_formatted }}</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${{ maintenanceTotalAmount }}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div class="flex space-x-2">
                                            <button v-if="!maintenanceSubscription.is_cancellation_requested && !maintenanceHasPropertyList"
                                                    :disabled="isSubmitting || hasActiveProtectSubscription"
                                                    class="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    @click="$emit('cancel', 'maintenance')">
                                                Cancel
                                            </button>
                                            <button v-else-if="maintenanceSubscription.is_cancellation_requested"
                                                    :disabled="isSubmitting || hasActiveProtectSubscription"
                                                    class="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    @click="$emit('resume-maintenance')">
                                                Resume
                                            </button>
                                            <span v-if="hasActiveProtectSubscription" class="text-xs text-gray-500 italic">Included in RiskGuard</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-for="prop in maintenanceFeature?.properties || []" :key="`mt-prop-${prop.id}`" class="bg-gray-50">
                                    <td class="pl-16 pr-6 py-3 whitespace-nowrap text-sm text-gray-700">{{ prop.full_address }}</td>
                                    <td colspan="3" class="px-6 py-3"></td>
                                    <td class="px-6 py-3 whitespace-nowrap text-sm font-medium">
                                        <button :disabled="isSubmitting"
                                                class="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="$emit('cancel-property', 'maintenance', prop)">
                                            Cancel
                                        </button>
                                    </td>
                                </tr>
                            </template>

                            <!-- Legacy base RiskGuard -->
                            <tr v-if="isProtectActive" class="hover:bg-gray-50">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-8 w-8">
                                            <font-awesome-icon :icon="faShieldExclamation" class="text-purple-600" />
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                                                <span>RiskGuard</span>
                                                <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                                                    {{ isProtectAnnualActive ? 'Yearly' : 'Monthly' }}
                                                </span>
                                            </div>
                                            <div class="text-sm text-gray-500">Partial access, plus discounted verified reports.</div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span :class="protectSubscription.is_cancellation_requested ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'"
                                        class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                                        {{ protectSubscription.is_cancellation_requested ? 'Cancellation Requested' : 'Active' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <span v-if="protectSubscription.is_cancellation_requested">
                                        Cancels on {{ protectSubscription.cancel_at ? new Date(protectSubscription.cancel_at).toLocaleDateString() : protectSubscription.stripe_sub_ends_at }}
                                    </span>
                                    <span v-else>{{ protectSubscription.stripe_sub_ends_at }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <span v-if="isProtectAnnualActive">${{ (protectSubscription.plan_amount / 100).toFixed(2) }} / year</span>
                                    <span v-else>${{ (protectSubscription.plan_amount / 100).toFixed(2) }} / month</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button v-if="!protectSubscription.is_cancellation_requested"
                                            :disabled="isSubmitting"
                                            class="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                            @click="$emit('cancel', 'protect')">
                                        Cancel
                                    </button>
                                    <button v-else
                                            :disabled="isSubmitting"
                                            class="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                            @click="$emit('resume-protect')">
                                        Resume
                                    </button>
                                </td>
                            </tr>

                            <!-- Legacy RiskGuard Pro -->
                            <tr v-if="isProtectProActive" class="hover:bg-gray-50">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-8 w-8">
                                            <font-awesome-icon :icon="faShieldExclamation" class="text-purple-600" />
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                                                <span>RiskGuard Pro</span>
                                                <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                                                    {{ isProtectProAnnualActive ? 'Yearly' : 'Monthly' }}
                                                </span>
                                            </div>
                                            <div class="text-sm text-gray-500">Full access, plus 2 monthly verified reports.</div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span :class="protectSubscription.is_cancellation_requested ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'"
                                        class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                                        {{ protectSubscription.is_cancellation_requested ? 'Cancellation Requested' : 'Active' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <span v-if="protectSubscription.is_cancellation_requested">
                                        Cancels on {{ protectSubscription.cancel_at ? new Date(protectSubscription.cancel_at).toLocaleDateString() : protectSubscription.stripe_sub_ends_at }}
                                    </span>
                                    <span v-else>{{ protectSubscription.stripe_sub_ends_at }}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <span v-if="isProtectProAnnualActive">${{ (protectSubscription.plan_amount / 100).toFixed(2) }} / year</span>
                                    <span v-else>${{ (protectSubscription.plan_amount / 100).toFixed(2) }} / month</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button v-if="!protectSubscription.is_cancellation_requested"
                                            :disabled="isSubmitting"
                                            class="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                            @click="$emit('cancel', 'protect-pro')">
                                        Cancel
                                    </button>
                                    <button v-else
                                            :disabled="isSubmitting"
                                            class="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                            @click="$emit('resume-protect')">
                                        Resume
                                    </button>
                                    <div v-if="protectSubscription.pending_subscription_type === 'rentrisk-protect'" class="mt-2">
                                        <div class="text-xs text-blue-600">
                                            Pending downgrade<span v-if="protectSubscription.pending_change_date"> on {{ new Date(protectSubscription.pending_change_date).toLocaleDateString() }}</span>
                                        </div>
                                        <button type="button"
                                                :disabled="isSubmitting"
                                                class="mt-1 text-xs text-indigo-600 hover:text-indigo-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="$emit('cancel-pending-downgrade')">
                                            Undo downgrade
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { faCommentsDollar, faShieldExclamation, faWrench } from '@fortawesome/pro-light-svg-icons';

export default {
    name: 'LegacySubscriptionsCard',
    props: {
        rentPaymentSubscription: { type: Object, default: null },
        maintenanceSubscription: { type: Object, default: null },
        rentPaymentFeature: { type: Object, default: null },
        maintenanceFeature: { type: Object, default: null },
        protectSubscription: { type: Object, default: null },
        protectAnnualPrice: { type: Number, default: null },
        protectProAnnualPrice: { type: Number, default: null },
        isSubmitting: { type: Boolean, default: false },
    },
    emits: ['cancel', 'resume-rent-payment', 'resume-maintenance', 'resume-protect', 'cancel-property', 'cancel-pending-downgrade'],
    data() {
        return {
            faCommentsDollar,
            faShieldExclamation,
            faWrench,
        };
    },
    computed: {
        hasActiveProtectSubscription() {
            return this.protectSubscription &&
                (['active', 'trialing'].includes(this.protectSubscription.stripe_sub_status) ||
                    this.protectSubscription.is_cancellation_requested);
        },
        rentPaymentHasPropertyList() {
            return (this.rentPaymentFeature?.properties?.length ?? 0) > 0;
        },
        maintenanceHasPropertyList() {
            return (this.maintenanceFeature?.properties?.length ?? 0) > 0;
        },
        rentPaymentTotalAmount() {
            if (!this.rentPaymentSubscription) {
                return null;
            }
            const quantity = this.rentPaymentHasPropertyList
                ? Math.max(1, this.rentPaymentFeature.billable_quantity)
                : 1;
            return ((this.rentPaymentSubscription.plan_amount / 100) * quantity).toFixed(2);
        },
        maintenanceTotalAmount() {
            if (!this.maintenanceSubscription) {
                return null;
            }
            const quantity = this.maintenanceHasPropertyList
                ? Math.max(1, this.maintenanceFeature.billable_quantity)
                : 1;
            return ((this.maintenanceSubscription.plan_amount / 100) * quantity).toFixed(2);
        },
        isProtectActive() {
            return this.protectSubscription &&
                this.protectSubscription.subscription_type === 'rentrisk-protect' &&
                (['active', 'trialing'].includes(this.protectSubscription.stripe_sub_status) ||
                    this.protectSubscription.is_cancellation_requested);
        },
        isProtectProActive() {
            return this.protectSubscription &&
                this.protectSubscription.subscription_type === 'rentrisk-protect-pro' &&
                (['active', 'trialing'].includes(this.protectSubscription.stripe_sub_status) ||
                    this.protectSubscription.is_cancellation_requested);
        },
        isProtectAnnualActive() {
            if (!this.isProtectActive || !this.protectAnnualPrice) {
                return false;
            }
            return Math.abs((this.protectSubscription.plan_amount / 100) - this.protectAnnualPrice) < 0.01;
        },
        isProtectProAnnualActive() {
            if (!this.isProtectProActive || !this.protectProAnnualPrice) {
                return false;
            }
            return Math.abs((this.protectSubscription.plan_amount / 100) - this.protectProAnnualPrice) < 0.01;
        },
    },
};
</script>
